<template>
  <div
    v-if="!formHidden"
    class="onboarding-data-entry"
  >
    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      ref="cardEl"
      class="onboarding-data-entry__card"
    >
      <!-- Sticky on desktop so the step and progress stay visible while scrolling. -->
      <div
        v-if="showFormScrapeProgress || formSteps"
        class="onboarding-data-entry__header"
      >
        <div
          v-if="showFormScrapeProgress"
          class="onboarding-data-entry__scrape-bar"
        >
          <ScrapeStatusBar
            :progress="formScrapeProgress"
            @dismiss="aiStore.dismissScrapeProgressPanel()"
          />
        </div>
        <OnboardingFormStepper
          v-if="formSteps"
          class="px-6 px-md-8 pt-6 pt-md-8 pb-4"
          :steps="stepperSteps"
          :current-index="formSteps.currentIndex.value"
          :required-progress="formSteps.requiredProgress.value"
          @select="formSteps.goTo($event)"
        />
      </div>
      <div
        class="onboarding-data-entry__body px-6 px-md-8 pb-8 pb-md-10"
        :class="formSteps ? 'pt-6' : 'pt-6 pt-md-8'"
      >
        <!-- Steps stay mounted (v-show) so in-progress uploads/dialogs survive navigation. -->
        <div
          v-show="isStepVisible('company')"
          data-form-step="company"
        >
          <p class="text-body-2 text-medium-emphasis mb-6">{{ stepHint('company') }}</p>
          <CompanyNameWebsiteSection />
          <AddressSection />
          <BrokerageLicenseSection v-if="isUsOnboarding" />
          <LegalUrlsSection />
        </div>
        <div
          v-show="isStepVisible('contacts')"
          data-form-step="contacts"
        >
          <p class="text-body-2 text-medium-emphasis mb-6">{{ stepHint('contacts') }}</p>
          <ContactPersonSection />
          <AmbassadorSection />
        </div>
        <div
          v-show="isStepVisible('brand')"
          data-form-step="brand"
        >
          <p class="text-body-2 text-medium-emphasis mb-6">{{ stepHint('brand') }}</p>
          <ProfileCopySection />
          <BrandColorsSection />
          <CompanyImagesSection
            :wizard-company="wizardCompany"
            @image-uploaded="emit('image-uploaded', $event)"
          />
        </div>
        <div
          v-show="isStepVisible('connections')"
          data-form-step="connections"
        >
          <p class="text-body-2 text-medium-emphasis mb-6">{{ stepHint('connections') }}</p>
          <ConnectionsSection />
          <LegalIntegrationsSection
            :wizard-company="wizardCompany"
            :legal-consents="legalConsents"
            @update:legal-consents="emit('update:legalConsents', $event)"
            @widget-integration="emit('widget-integration', $event)"
          />
        </div>
        <MissingFieldsSummary
          v-if="validation"
          :areas="validation.missingFieldsByArea.value"
          :revealed="validation.missingFieldsRevealed.value"
          class="mt-6"
        />
        <div
          class="d-flex flex-wrap justify-space-between align-center ga-3 onboarding-enter-data-actions"
        >
          <v-btn
            variant="outlined"
            rounded="lg"
            @click="onBack"
          >
            {{ $t('onboarding.stepContainer.back') }}
          </v-btn>
          <v-btn
            color="primary"
            rounded="lg"
            size="large"
            class="onboarding-enter-data-actions__continue"
            data-testid="onboarding-form-step-continue"
            @click="onContinue"
          >
            {{
              formSteps && !formSteps.isLast.value
                ? $t('onboarding.wizard.form-steps.next')
                : continueLabel
            }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, provide, reactive, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { LegalConsentsValues } from '@/components/ai/onboarding/OnboardingLegalConsents.vue'
import ScrapeStatusBar from '@/components/ai/onboarding/ScrapeStatusBar.vue'
import { buildScrapeProgressStartingView } from '@/lib/ai/scrapeProgress'

import AddressSection from './components/data-entry/AddressSection.vue'
import AmbassadorSection from './components/data-entry/AmbassadorSection.vue'
import BrandColorsSection from './components/data-entry/BrandColorsSection.vue'
import BrokerageLicenseSection from './components/data-entry/BrokerageLicenseSection.vue'
import CompanyImagesSection from './components/data-entry/CompanyImagesSection.vue'
import CompanyNameWebsiteSection from './components/data-entry/CompanyNameWebsiteSection.vue'
import ConnectionsSection from './components/data-entry/ConnectionsSection.vue'
import ContactPersonSection from './components/data-entry/ContactPersonSection.vue'
import LegalIntegrationsSection from './components/data-entry/LegalIntegrationsSection.vue'
import LegalUrlsSection from './components/data-entry/LegalUrlsSection.vue'
import MissingFieldsSummary from './components/data-entry/MissingFieldsSummary.vue'
import ProfileCopySection from './components/data-entry/ProfileCopySection.vue'
import OnboardingFormStepper from './components/OnboardingFormStepper.vue'
import { onboardingDataEntryKey } from './composables/onboardingDataEntryContext'
import { useOnboardingValidationContext } from './composables/onboardingValidationContext'
import { useOnboardingFeatures } from './composables/useOnboardingFeatures'
import { useOnboardingFieldChrome } from './composables/useOnboardingFieldChrome'
import {
  type BrandColorField,
  getBrandColorIssue,
  getFirstBrandColorIssue,
  isBrandColorField,
} from './lib/brandColorValidation'
import { ONBOARDING_FORM_STEPS, type OnboardingFormStepKey } from './lib/onboardingFormSteps'
import { resolveOnboardingPhoneCountry } from './lib/onboardingPhoneValidation'
import type { OnboardingFormData } from './lib/onboardingPrefill'
import type { OnboardingWizardCompany } from './lib/onboardingWizardCompany'

export type { OnboardingFormData } from './lib/onboardingPrefill'
export type {
  OnboardingWizardCompany,
  OnboardingWizardCompanyImage,
} from './lib/onboardingWizardCompany'

const props = defineProps<{
  formData: OnboardingFormData
  aiCapturedFields: Set<string>
  /** Apollo `company` (avoid naming collision with `company` from `@/lib/features`). */
  wizardCompany?: OnboardingWizardCompany | null
  /**
   * When true the entire form is hidden and the user works through the chat assistant only.
   * Field focus scrolling is also suppressed.
   */
  formHidden?: boolean
  /** Legal-consent state, owned by the onboarding wizard (index.vue) and shared with the Review step. */
  legalConsents?: LegalConsentsValues | null
  /**
   * Shared, session-aware US-market flag from `useOnboardingForm` (index.vue). Recomputing it here
   * would drop the Genaisis `session.market` signal and desync field visibility from validation.
   */
  isUsOnboarding: boolean
  /** User ticked "I don't have a website" — the website field is hidden. */
  hasNoWebsite: boolean
  continueLabel: string
}>()

const emit = defineEmits<{
  'update:field': [field: string, value: string | boolean]
  'image-uploaded': [payload: { kind: 'logo' | 'team' | 'profile'; url?: string }]
  'widget-integration': [payload: { enabled: boolean }]
  'update:legalConsents': [value: LegalConsentsValues]
  back: []
  continue: []
}>()

const { isAssistedOnboardingEnabled, isOnboardingWebScrapingEnabled } = useOnboardingFeatures()

const { aiStore, fieldClass, onFieldFocus } = useOnboardingFieldChrome({
  formHidden: () => Boolean(props.formHidden),
})
const { scrapeRunning, scrapeProgressPanelView, requestedFieldFocus } = storeToRefs(aiStore)

const formScrapeProgress = computed(() => {
  if (!isOnboardingWebScrapingEnabled.value) return null
  if (isAssistedOnboardingEnabled.value) return null
  if (scrapeProgressPanelView.value) return scrapeProgressPanelView.value
  if (scrapeRunning.value) return buildScrapeProgressStartingView()
  return null
})

const showFormScrapeProgress = computed(() => Boolean(formScrapeProgress.value))

const updateField = (field: string, value: string | boolean) => {
  // Single choke point for manual edits: an inaccessible brand color is never emitted,
  // whichever section tries to write it.
  if (
    isBrandColorField(field) &&
    getBrandColorIssue(field, typeof value === 'string' ? value : '')
  ) {
    return
  }
  aiStore.dismissFieldAiSuggestion(field)
  emit('update:field', field, value)
}

const validation = useOnboardingValidationContext()
// Fields the user has left at least once; their errors show from then on.
const touchedFields = reactive(new Set<string>())

const markFieldTouched = (key: string) => {
  touchedFields.add(key)
}

const visibleFieldError = (key: string) => {
  if (!validation) return ''
  if (!validation.missingFieldsRevealed.value && !touchedFields.has(key)) return ''
  return validation.fieldError(key)
}

const { t } = useI18n()
const cardEl = ref<{ $el?: HTMLElement } | null>(null)
const formSteps = validation?.formSteps ?? null

const stepperSteps = computed(() =>
  ONBOARDING_FORM_STEPS.map(({ key, labelKey }) => ({
    key,
    label: t(labelKey),
    complete: formSteps?.status.value[key].complete ?? false,
  }))
)

// Without a validation context (should not happen in the wizard) show the whole form.
const isStepVisible = (step: OnboardingFormStepKey) =>
  !formSteps || formSteps.currentStep.value === step

const stepHint = (step: OnboardingFormStepKey) =>
  t(ONBOARDING_FORM_STEPS.find(({ key }) => key === step)?.hintKey ?? '')

// A typed color that fails stays in its field until it is corrected or canceled.
const brandColorDrafts = reactive<Record<BrandColorField, string | null>>({
  primaryColor: null,
  secondaryColor: null,
})

// Stored values can still fail (AI capture, older data), and so can a draft. Don't let the user
// leave the brand step (or finish the form) with them; take them to the field instead, where
// BrandColorsSection shows the reason.
const brandColorIssue = computed(() =>
  getFirstBrandColorIssue({
    primaryColor: brandColorDrafts.primaryColor ?? props.formData.primaryColor,
    secondaryColor: brandColorDrafts.secondaryColor ?? props.formData.secondaryColor,
  })
)

const onContinue = async () => {
  const blocked = brandColorIssue.value
  const leavingBrandColors =
    !formSteps || formSteps.isLast.value || formSteps.currentStep.value === 'brand'
  if (!blocked || !leavingBrandColors) {
    if (!formSteps || formSteps.isLast.value) emit('continue')
    else formSteps.next()
    return
  }
  formSteps?.show('brand')
  await nextTick()
  const input = document.getElementById(`onboarding-field-${blocked.field}`)
  input?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  input?.focus({ preventScroll: true })
}

const onBack = () => {
  if (formSteps && formSteps.currentIndex.value > 0) formSteps.previous()
  else emit('back')
}

// Bring the new step's top (stepper) into view after navigating.
watch(
  () => formSteps?.currentIndex.value,
  async () => {
    await nextTick()
    cardEl.value?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
)

// The assistant can ask to focus a field on another step: show that step first
// (pre-flush) so useOnboardingFieldChrome's post-flush scroll finds it visible.
watch(requestedFieldFocus, (key) => {
  if (!key || !formSteps) return
  const step = document
    .querySelector(`[data-ai-field="${key}"]`)
    ?.closest('[data-form-step]')
    ?.getAttribute('data-form-step') as OnboardingFormStepKey | null | undefined
  if (step) formSteps.show(step)
})

provide(onboardingDataEntryKey, {
  formData: toRef(props, 'formData'),
  aiCapturedFields: toRef(props, 'aiCapturedFields'),
  updateField,
  onFieldFocus,
  fieldClass,
  isUsOnboarding: toRef(props, 'isUsOnboarding'),
  hasNoWebsite: toRef(props, 'hasNoWebsite'),
  visibleFieldError,
  markFieldTouched,
  isFieldRequired: (key: string) => validation?.isFieldRequired(key) ?? false,
  defaultPhoneCountry: computed(() =>
    resolveOnboardingPhoneCountry(props.isUsOnboarding, props.formData.country, t)
  ),
  brandColorDrafts,
})
</script>

<style scoped>
.onboarding-data-entry {
  width: 100%;
}

.onboarding-data-entry__card {
  /* `clip` keeps the rounded corners without making the card a scroll container,
     which would stop the header from sticking to the scrolling form column. */
  overflow: clip;
}

.onboarding-data-entry__header {
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), 0.12);
}

@media (min-width: 960px) {
  .onboarding-data-entry__header {
    position: sticky;
    top: 0;
    /* Above the bottom actions (1300) that scroll underneath it. */
    z-index: 1310;
  }
}

.onboarding-data-entry__scrape-bar :deep(.scrape-status-bar) {
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
}

.onboarding-enter-data-actions {
  position: relative;
  z-index: 1300;
  flex-shrink: 0;
  width: 100%;
  padding-top: 20px;
  padding-bottom: 4px;
}

/* Sit above the ProductFruits "Support & Tipps" launcher (z-index 1200). */
.onboarding-enter-data-actions__continue {
  position: relative;
  z-index: 1300;
}

:deep(.onboarding-field--ai-updated .v-field__outline) {
  --v-field-border-opacity: 1;
  color: rgb(var(--v-theme-primary));
}

:deep(.onboarding-field--ai-focus .v-field) {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.25);
}
</style>
